import React, { FC, useCallback, useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import { useToasts } from 'react-toast-notifications';
import Toasts from '../../../components/bootstrap/Toasts';
import { forgetPassword, getCountry } from '../../../apis';
import AuthContext from '../../../contexts/authContext';
import Spinner from '../../../components/bootstrap/Spinner';
import { useFormik } from 'formik';
import ConfigContext from '../../../contexts/configContext';
import useDarkMode from '../../../hooks/useDarkMode';
import PhoneInput from 'react-phone-input-2';
import Checks from '../../../components/bootstrap/forms/Checks';
import 'react-phone-input-2/lib/style.css';
import { EXCLUDED_PHONE_COUNTRIES } from '../../../constants/countryFilters';
const ForgotPassword: FC = () => {
	const { darkModeStatus } = useDarkMode();
	const { addToast } = useToasts();

	const navigate = useNavigate();
	const handleContinue = useCallback(
		(userName: string, mode: 'text' | 'mobile') =>
			navigate('/auth-pages/resetpassword', { state: { userName, usernameMode: mode } }),
		[navigate],
	);
	const [countryCode, setCountryCode] = useState<string>('');
	const [usernameMode, setUsernameMode] = useState<'text' | 'mobile'>(
		process.env.REACT_APP_HIDE_USERNAME === 'true' ? 'mobile' : 'text',
	);
	const backgroundColor = darkModeStatus ? '#212529' : '#f8f9fa';
	const borderColor = darkModeStatus ? '#343a40' : '#f8f9fa';
	const isMobileMode = usernameMode === 'mobile';

	useEffect(() => {
		const fetchCountryCode = async () => {
			try {
				const response = await getCountry();
				const detectedCountryCode =
					response?.country || response?.country_code || response?.countryCode;
				if (detectedCountryCode) {
					const normalizedCode = detectedCountryCode.toUpperCase();
					const country_code =
						process.env.REACT_APP_STATIC_COUNTRY_CODE || normalizedCode;
					setCountryCode(country_code);
				}
			} catch (error) {
				console.warn('Unable to detect country automatically', error);
				setCountryCode('IN');
			}
		};

		fetchCountryCode();
	}, []);
	const disableCountrySelect =
		process.env.REACT_APP_STATIC_COUNTRY_CODE !== undefined &&
		process.env.REACT_APP_STATIC_COUNTRY_CODE !== '';
	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			userName: '',
		},
		validate: (values) => {
			const errors: {
				emailToSend?: string;
				userName?: string;
			} = {};
			if (!values.userName) {
				errors.userName = 'Required';
			} else if (values.userName.length < 7 && isMobileMode) {
				errors.userName = 'Minimum of 7 characters are needed';
			}
			return errors;
		},
		validateOnChange: false,
		onSubmit: async (values) => {
			setIsLoading(true);
			const arkbaseurl = process.env.REACT_APP_ARKAPIURL;
			const MobileAuthApi = process.env.REACT_APP_MOBILE_AUTH_API;
			let data = null;
			data = await forgetPassword(arkbaseurl, values.userName);
			if (data && data.success === true) {
				setIsLoading(false);
				handleContinue(values.userName.trim(), usernameMode);
				addToast(
					<Toasts title='Suceess' icon='VerifiedUser' iconColor='warning' isDismiss>
						{data.message}
					</Toasts>,
					{
						autoDismiss: true,
					},
				);
			} else {
				setIsLoading(false);
				addToast(
					<Toasts title='Failed' icon='Error' time='Now' isDismiss>
						{data.message}
					</Toasts>,
					{
						autoDismiss: true,
					},
				);
			}
		},
	});

	const [isLoading, setIsLoading] = useState<boolean>(false);

	const handleReturnToLogin = useCallback(() => navigate('/auth-pages/login'), [navigate]);

	const handleKeyPress = (e: any) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			formik.handleSubmit();
		}
	};

	return (
		<PageWrapper isProtected={false} title={'Login'} className={'bg-dark'}>
			<Page className='p-0'>
				<div className='row h-100 align-items-center justify-content-center'>
					<div className='col-xl-4 col-lg-6 col-md-8 shadow-3d-container'>
						<Card
							className='shadow-3d-dark'
							data-tour='login-page'
							style={{ backgroundColor: '#2a2d36' }}>
							<CardBody
								className={classNames('rounded-3', {
									'bg-l10-dark': !darkModeStatus,
									'bg-l1-dark': darkModeStatus,
								})}>
								<div className='text-center my-5'>
									<Link
										to='/auth-pages/forgotpassword'
										className={
											'text-decoration-none  fw-bold display-2 text-dark'
										}>
										<img src='/logo192.png' width={150} />
									</Link>
								</div>

								<div
									className={classNames('text-center', 'h1', 'fw-bold', 'mt-5', {
										'text-dark': !darkModeStatus,
										'text-white': darkModeStatus,
									})}>
									Reset your password,
								</div>
								<div className='text-center h4 text-muted mb-5'>
									We'll{' '}
									{isMobileMode
										? 'send you OTP to your mobile'
										: 'email you instructions'}{' '}
									to reset the password.
								</div>

								<form className='row g-4'>
									<div className='col-12'>
										{!isMobileMode ? (
											<FormGroup
												id='userName'
												isFloating
												label='username or email'>
												<Input
													value={formik.values.userName}
													isTouched={formik.touched.userName}
													invalidFeedback={formik.errors.userName}
													isValid={formik.isValid}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													onFocus={() => {
														formik.setErrors({});
													}}
													onKeyDown={handleKeyPress}
												/>
											</FormGroup>
										) : (
											<div className='col-12'>
												<FormGroup
													id='userName'
													isFloating
													style={{ backgroundColor: 'transparent' }}>
													<PhoneInput
														country={countryCode.toLowerCase()}
														excludeCountries={EXCLUDED_PHONE_COUNTRIES}
														placeholder='Enter Mobile number'
														value={formik.values.userName}
														onBlur={() =>
															formik.setFieldTouched('userName', true)
														}
														disableCountryCode={disableCountrySelect}
														disableCountryGuess={disableCountrySelect}
														disableDropdown={disableCountrySelect}
														onFocus={() => {
															formik.setErrors({});
														}}
														onChange={(value) =>
															formik.setFieldValue('userName', value)
														}
														inputStyle={{
															width: '100%',
															height: 'calc(3.5rem + 2px)',
															borderRadius: '1rem',
															border: '1px solid #f8f9fa',
															backgroundColor: backgroundColor,
															borderColor: borderColor,
														}}
													/>
													<Input
														type='hidden'
														autoComplete='userName'
														value={formik.values.userName}
														isTouched={formik.touched.userName}
														invalidFeedback={formik.errors.userName}
														isValid={formik.isValid}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
														onFocus={() => {
															formik.setErrors({});
														}}
													/>
												</FormGroup>
											</div>
										)}
									</div>
									<div className='col-12'>
										<div
											className={classNames(
												'd-flex',
												'align-items-center justify-content-center',
												'gap-2',
												{
													'text-dark': !darkModeStatus,
													'text-white': darkModeStatus,
												},
											)}>
											<span className='small text-muted'>
												{isMobileMode
													? 'Use Username instead'
													: 'Use Mobile instead'}
											</span>
											<Checks
												id='forgotPasswordUsernameMode'
												type='switch'
												name='forgotPasswordUsernameMode'
												ariaLabel='Username input type switch'
												checked={isMobileMode}
												onChange={() => {
													setUsernameMode((prev) =>
														prev === 'mobile' ? 'text' : 'mobile',
													);
													formik.setErrors({});
													formik.setValues({ userName: '' });
													formik.setTouched({});
												}}
											/>
										</div>
									</div>
									<div
										className={classNames(
											'col-12',
											'd-flex',
											'justify-content-end',
											{
												'text-dark': !darkModeStatus,
												'text-white': darkModeStatus,
											},
										)}>
										<span
											style={{ cursor: 'pointer' }}
											onClick={handleReturnToLogin}>
											Return to Login
										</span>
									</div>

									<div className='col-12'>
										<Button
											color='info'
											className='w-100 py-3'
											onClick={formik.handleSubmit}>
											{isLoading && <Spinner isSmall inButton isGrow />}
											Send
										</Button>
									</div>
								</form>
							</CardBody>
						</Card>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default ForgotPassword;
